import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './Invoices.css';
import { localToday } from '../../utils/dateUtils';

// Amounts come from the API as strings or numbers; compare them in cents
const num = (value) => parseFloat(value) || 0;
const cents = (value) => Math.round(num(value) * 100);
const today = () => localToday();

const PaymentDialog = ({ invoice, isOpen, onClose, onSave }) => {
  const { t } = useTranslation('sales');
  const initialPayment = () => ({
    payment_amount: num(invoice?.amount_due).toFixed(2),
    payment_method: t('invoices.payment.dialog.methods.bankTransfer'),
    payment_date: today(),
    payment_reference: '',
    notes: ''
  });
  const [paymentData, setPaymentData] = useState(initialPayment);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen) return null;

  const handleInputChange = (field, value) => {
    setPaymentData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validatePayment = () => {
    const newErrors = {};

    if (cents(paymentData.payment_amount) <= 0) {
      newErrors.payment_amount = t('invoices.payment.dialog.validation.amountRequired');
    } else if (cents(paymentData.payment_amount) > cents(invoice.amount_due)) {
      newErrors.payment_amount = t('invoices.payment.dialog.validation.amountExceedsBalance');
    }

    if (!paymentData.payment_method) {
      newErrors.payment_method = t('invoices.payment.dialog.validation.methodRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validatePayment()) {
      return;
    }

    setIsLoading(true);
    try {
      // The backend derives PARTIAL / PAID from the balance
      await onSave({
        payment_amount: (cents(paymentData.payment_amount) / 100).toFixed(2),
        payment_method: paymentData.payment_method,
        payment_date: paymentData.payment_date,
        payment_reference: paymentData.payment_reference,
        notes: paymentData.notes
      });
      onClose();
    } catch (error) {
      console.error('Error recording payment:', error);
      // Error handling will be done by parent component
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setPaymentData(initialPayment());
    setErrors({});
    onClose();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const settles = cents(paymentData.payment_amount) >= cents(invoice?.amount_due);

  const paymentMethods = [
    { value: t('invoices.payment.dialog.methods.bankTransfer'), label: t('invoices.payment.dialog.methods.bankTransfer') },
    { value: t('invoices.payment.dialog.methods.creditCard'), label: t('invoices.payment.dialog.methods.creditCard') },
    { value: t('invoices.payment.dialog.methods.debitCard'), label: t('invoices.payment.dialog.methods.debitCard') },
    { value: t('invoices.payment.dialog.methods.check'), label: t('invoices.payment.dialog.methods.check') },
    { value: t('invoices.payment.dialog.methods.cash'), label: t('invoices.payment.dialog.methods.cash') },
    { value: t('invoices.payment.dialog.methods.wireTransfer'), label: t('invoices.payment.dialog.methods.wireTransfer') },
    { value: t('invoices.payment.dialog.methods.paypal'), label: t('invoices.payment.dialog.methods.paypal') },
    { value: t('invoices.payment.dialog.methods.other'), label: t('invoices.payment.dialog.methods.other') }
  ];

  return (
    <div className="modal-overlay">
      <div className="modal-dialog payment-dialog">
        <div className="modal-header">
          <h3>{t('invoices.payment.dialog.title')}</h3>
          <button
            className="modal-close"
            onClick={handleCancel}
            disabled={isLoading}
          >
            ×
          </button>
        </div>

        <div className="modal-body">
          {/* Invoice Summary */}
          <div className="payment-summary">
            <div className="summary-row">
              <span className="label">{t('invoices.view.fields.invoiceNumber')}:</span>
              <span className="value">{invoice?.invoice_number}</span>
            </div>
            <div className="summary-row">
              <span className="label">{t('invoices.view.fields.customer')}:</span>
              <span className="value">{invoice?.customer_name}</span>
            </div>
            <div className="summary-row">
              <span className="label">{t('invoices.view.fields.totalAmount')}:</span>
              <span className="value">{formatCurrency(invoice?.total_amount)}</span>
            </div>
            <div className="summary-row">
              <span className="label">{t('invoices.view.fields.amountPaid')}:</span>
              <span className="value">{formatCurrency(invoice?.amount_paid)}</span>
            </div>
            <div className="summary-row total-due">
              <span className="label">{t('invoices.view.fields.amountDue')}:</span>
              <span className="value">{formatCurrency(invoice?.amount_due)}</span>
            </div>
          </div>

          {/* Payment Form */}
          <div className="payment-form">
            <div className="form-row">
              <div className="field-group">
                <label>{t('invoices.payment.dialog.fields.amount')} *</label>
                <input
                  type="number"
                  value={paymentData.payment_amount}
                  onChange={(e) => handleInputChange('payment_amount', e.target.value)}
                  min="0"
                  max={num(invoice?.amount_due)}
                  step="0.01"
                  aria-label={t('invoices.payment.dialog.fields.amount')}
                  className={errors.payment_amount ? 'error' : ''}
                  disabled={isLoading}
                />
                {errors.payment_amount && (
                  <div className="error-text">{errors.payment_amount}</div>
                )}
              </div>

              <div className="field-group">
                <label>{t('invoices.payment.dialog.fields.paymentMethod')} *</label>
                <select
                  value={paymentData.payment_method}
                  onChange={(e) => handleInputChange('payment_method', e.target.value)}
                  className={errors.payment_method ? 'error' : ''}
                  disabled={isLoading}
                >
                  {paymentMethods.map(method => (
                    <option key={method.value} value={method.value}>{method.label}</option>
                  ))}
                </select>
                {errors.payment_method && (
                  <div className="error-text">{errors.payment_method}</div>
                )}
              </div>
            </div>

            <div className="field-group">
              <label>{t('invoices.payment.dialog.fields.paymentDate')}</label>
              <input
                type="date"
                value={paymentData.payment_date}
                onChange={(e) => handleInputChange('payment_date', e.target.value)}
                aria-label={t('invoices.payment.dialog.fields.paymentDate')}
                disabled={isLoading}
              />
            </div>

            <div className="field-group">
              <label>{t('invoices.payment.dialog.fields.reference')}</label>
              <input
                type="text"
                value={paymentData.payment_reference}
                onChange={(e) => handleInputChange('payment_reference', e.target.value)}
                placeholder={t('invoices.payment.dialog.placeholders.enterReference')}
                disabled={isLoading}
              />
            </div>

            <div className="field-group">
              <label>{t('invoices.payment.dialog.fields.notes')}</label>
              <textarea
                value={paymentData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder={t('invoices.payment.dialog.placeholders.enterNotes')}
                rows="3"
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Payment Impact */}
          <div className="payment-impact">
            <div className="impact-row">
              <span className="label">{t('invoices.payment.dialog.impact.newAmountPaid')}:</span>
              <span className="value">
                {formatCurrency(num(invoice?.amount_paid) + num(paymentData.payment_amount))}
              </span>
            </div>
            <div className="impact-row">
              <span className="label">{t('invoices.payment.dialog.impact.remainingBalance')}:</span>
              <span className="value">
                {formatCurrency(Math.max(0, num(invoice?.amount_due) - num(paymentData.payment_amount)))}
              </span>
            </div>
            <div className="impact-row">
              <span className="label">{t('invoices.payment.dialog.impact.newStatus')}:</span>
              <span className={`value status-${settles ? 'paid' : 'partial'}`}>
                {settles ? t('invoices.view.paymentStatus.paid') : t('invoices.view.paymentStatus.partial')}
              </span>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button
            onClick={handleCancel}
            className="btn-secondary"
            disabled={isLoading}
          >
            {t('invoices.payment.dialog.buttons.cancel')}
          </button>
          <button
            onClick={handleSave}
            className="btn-primary"
            disabled={isLoading}
          >
            {isLoading ? t('invoices.payment.dialog.buttons.recording') : t('invoices.payment.dialog.buttons.record')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentDialog;